import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Settings, FileText, Globe, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';
import LogoSite from '../Logo/LogoSite.jpeg';

interface LayoutProps {
  children: React.ReactNode;
  hideHeader?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, hideHeader = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState<string>(t('dashboard.userDefault'));
  const [showLanguageMenu, setShowLanguageMenu] = useState<boolean>(false);
  const [userAccess, setUserAccess] = useState<number | null>(null);

  useEffect(() => {
    const savedUsername = localStorage.getItem('username');
    if (savedUsername) {
      setUsername(savedUsername);
    }
    fetchUserData();
  }, []);

  const fetchUserData = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsername(data.username);
        localStorage.setItem('username', data.username);
        // Récupérer le niveau d'accès (peut être undefined si la colonne n'existe pas dans la réponse)
        console.log('=== USER ACCESS DEBUG ===');
        console.log('Full user data:', data);
        console.log('Access level value:', data.access);
        console.log('Access level type:', typeof data.access);
        console.log('Is access null?:', data.access === null);
        console.log('Is access undefined?:', data.access === undefined);
        console.log('========================');
        setUserAccess(data.access !== undefined ? data.access : null);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des données:', err);
    }
  };

  const handleLogout = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('rememberMe');
    window.location.href = '/login';
  };

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setShowLanguageMenu(false);
  };

  // Debug: Log userAccess state on every render
  useEffect(() => {
    console.log('=== LAYOUT RENDER DEBUG ===');
    console.log('userAccess state:', userAccess);
    console.log('userAccess type:', typeof userAccess);
    console.log('Condition: userAccess !== null:', userAccess !== null);
    console.log('Condition: userAccess <= 1:', userAccess !== null ? userAccess <= 1 : 'N/A (null)');
    console.log('Should show Users button:', userAccess !== null && userAccess <= 1);
    console.log('==========================');
  }, [userAccess]);

  // Sur mobile, ne pas afficher le header pour certaines pages (StudentAnswerTest)
  const shouldHideHeaderMobile = hideHeader || location.pathname.includes('/student-answer-test');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header - Masqué sur mobile pour StudentAnswerTest, toujours visible sur desktop */}
      <header className={`bg-white shadow-md ${shouldHideHeaderMobile ? 'hidden md:block' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <img
                src={LogoSite}
                alt="Logo SiteTeacher"
                className="w-20 h-20 rounded-full object-cover"
              />
              <h1 className="text-2xl font-bold text-gray-800">{t('common.appName')}</h1>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              <button
                onClick={() => navigate('/my-tests')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  location.pathname === '/my-tests'
                    ? 'bg-blue-100 text-blue-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-5 h-5" />
                {t('common.myTests')}
              </button>

              {/* Bouton Users - Visible uniquement si access === 0 ou access === 1 */}
              {userAccess !== null && userAccess <= 1 && (
                <button
                  onClick={() => navigate('/users')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    location.pathname === '/users'
                      ? 'bg-blue-100 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  Utilisateurs
                </button>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-gray-700 font-medium hidden md:inline">
              {username}
            </span>

            {/* Sélecteur de langue */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="text-gray-600 hover:text-gray-800 transition flex items-center gap-2"
                title={t('languages.' + i18n.language)}
              >
                <Globe className="w-6 h-6" />
                <span className="hidden md:inline text-sm font-medium">{i18n.language.toUpperCase()}</span>
              </button>

              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={() => changeLanguage('fr')}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition rounded-t-lg ${
                      i18n.language === 'fr' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    {t('languages.fr')}
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition rounded-b-lg ${
                      i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    {t('languages.en')}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/profile-settings')}
              className="text-gray-600 hover:text-gray-800 transition hidden md:block"
              title={t('header.profileSettings')}
            >
              <Settings className="w-6 h-6" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>
    </div>
  );
};

export default Layout;
